import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { CheckCircle2, XCircle, Loader2, MailCheck, ArrowRight } from "lucide-react";

export default function VerifyEmail() {
  const { token } = useParams();
  const [state, setState] = useState("verifying"); // verifying | ok | err
  const [msg, setMsg] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resendState, setResendState] = useState("idle");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/api/launch/verify/${token}`);
        if (data.ok) {
          setState("ok");
          setMsg(data.name ? `Welcome, ${data.name}.` : "You're verified.");
        } else {
          setState("err");
          setMsg(
            data.error === "token_expired" ? "This link has expired."
            : data.error === "token_already_used" ? "This link has already been used."
            : "This verification link is invalid."
          );
        }
      } catch (e) {
        setState("err");
        setMsg("We couldn't verify your email. Please try again.");
      }
    })();
  }, [token]);

  const resend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResendState("sending");
    try {
      await api.post("/api/launch/resend-verification", { email: resendEmail });
      setResendState("sent");
    } catch (e) {
      setResendState("err");
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center px-5" data-testid="verify-email-page">
      <div className="max-w-md w-full rounded-3xl border border-white/10 bg-white/[0.015] p-8 sm:p-10 text-center"
           style={{ backdropFilter: "blur(20px)" }}>
        {state === "verifying" && (
          <>
            <Loader2 className="w-8 h-8 mx-auto animate-spin text-white/60" />
            <div className="mt-4 text-white/70 text-sm">Verifying your email…</div>
          </>
        )}
        {state === "ok" && (
          <>
            <CheckCircle2 className="w-10 h-10 mx-auto text-[#00ff9c]" strokeWidth={1.6} />
            <div className="mt-4 text-[11px] font-mono tracking-[0.25em] uppercase text-[#00ff9c]">Verified</div>
            <h1 className="mt-2 text-2xl sm:text-3xl text-white font-semibold tracking-tight">
              You&apos;re on the FlowFinder waitlist.
            </h1>
            <p className="mt-3 text-white/60 text-[14px] leading-relaxed">
              {msg} We&apos;ll notify you the second FlowFinder launches — and your 3-day Pro trial
              will be waiting.
            </p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-white text-black font-semibold text-[13px] px-5 py-3 hover:bg-white/90 transition"
            >
              Back home <ArrowRight className="w-4 h-4" strokeWidth={2.4} />
            </Link>
          </>
        )}
        {state === "err" && (
          <>
            <XCircle className="w-10 h-10 mx-auto text-red-400" strokeWidth={1.6} />
            <div className="mt-4 text-[11px] font-mono tracking-[0.25em] uppercase text-red-400">Verification issue</div>
            <h1 className="mt-2 text-2xl text-white font-semibold tracking-tight">{msg}</h1>
            <p className="mt-3 text-white/50 text-[13px]">
              Enter your email and we&apos;ll send you a fresh confirmation link.
            </p>
            <form onSubmit={resend} className="mt-5 flex flex-col gap-3">
              <input
                type="email"
                required
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                placeholder="you@email.com"
                className="w-full rounded-xl bg-white/[0.03] border border-white/10 px-4 py-3 text-[14px] text-white placeholder-white/30 focus:outline-none focus:border-white/40 transition"
                data-testid="resend-email-input"
              />
              <button
                data-testid="resend-verify-btn"
                type="submit"
                disabled={resendState === "sending"}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-white text-black font-semibold text-[14px] px-6 py-3.5 hover:bg-white/90 transition disabled:opacity-60"
              >
                <MailCheck className="w-4 h-4" strokeWidth={2.2} />
                {resendState === "sending" ? "Sending…"
                  : resendState === "sent" ? "Sent — check your inbox"
                  : "Resend confirmation email"}
              </button>
            </form>
            {resendState === "sent" && (
              <div className="mt-3 text-[12px] text-[#00ff9c]">
                If your email is on our waitlist, a new confirmation link is on its way.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
